import { Typography } from '@material-ui/core';
import {
  DataGridPro,
  GridCellParams,
  GridFilterModel,
  GridSortModel,
  GridValueGetterParams,
  getGridStringOperators,
} from '@mui/x-data-grid-pro';
import { getInstrumentoFinanceiroLongDescription } from 'common/genericDescriptions';
import { GrupoAcesso, InstrumentoFinanceiroApiResponse, InstrumentosFinanceirosFilter, RegistroStatus } from 'common/genericTypes';
import { InstrumentosFinanceirosColumnType } from 'enums/instrumentosFinanceirosColumnType';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from 'store';
import CustomToolBarUtils from 'utils/dataGrid/customToolBar';
import { localizedTextsMap, componentsPropsValues, rowsPerPageOptionsValues } from 'utils/dataGrid/dataGridUtils';
import { formatEightDecimalsCurrency, formatEightDecimalsNumber } from 'utils/number';

const ListaDeSaldoAtivos: React.FC = () => {
  const rowsPerPageOptionsSaldoAtivos = rowsPerPageOptionsValues.filter(
    (value) => value !== 500 && value !== 750 && value !== 1000
  );
  const [instrumentoList, setInstrumentoList] = useState<Array<InstrumentoFinanceiroApiResponse>>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const dispatch = useDispatch();
  const postInstrumentosSaldosAtivosPaginated = useSelector(
    (state: RootState) => state.instrumentoApi.postInstrumentosSaldosAtivosPaginated
  );
  const [page, setPage] = useState<number>(0);
  const [rowCount, setRowCount] = useState<number>(0);
  const [pageSize, setPageSize] = useState<number>(20);
  const isParticipanteComum = useSelector((state: RootState) => state.auth.usuario.grupoAcesso === GrupoAcesso.Comum);
  const [instrumentosFinanceirosFilter, setInstrumentosFinanceirosFilter] = useState<
    Array<InstrumentosFinanceirosFilter>
  >([]);
  const [linkOperator, setLinkOperator] = useState<string>('AND');
  const lastRequestSeqRef = useRef<number>(0);
  const filterDebounceTimeoutRef = useRef<number | undefined>(undefined);

  const getColumnTypeForBackend = useCallback((columnField: string) => {
    const columnTypeMap: Record<string, number> = {
      codigo: InstrumentosFinanceirosColumnType.CODIGO,
      tipoInstrumentoFinanceiro: InstrumentosFinanceirosColumnType.TIPO_INSTRUMENTO_FINANCEIRO,
      'emissor.razaoSocial': InstrumentosFinanceirosColumnType.RAZAO_EMISSOR,
      'emissor.codigo': InstrumentosFinanceirosColumnType.CODIGO_PARTICIPANTE,
      'criteriosRemuneracao.quantidadeEmitida': InstrumentosFinanceirosColumnType.QUANTIDADE,
      'criteriosRemuneracao.valorNominalUnitarioEmissao': InstrumentosFinanceirosColumnType.VALOR_UNITARIO_EMISSAO,
    };

    return columnTypeMap[columnField] ?? columnField;
  }, []);

  const getInstrumentoList = useCallback(async () => {
    const requestSeq = ++lastRequestSeqRef.current;
    try {
      setLoading(true);
      const result = await postInstrumentosSaldosAtivosPaginated(
        dispatch,
        {
          registroStatus: [RegistroStatus.Aprovado],
          page: page + 1,
          pageSize: pageSize,
          linkOperator,
          instrumentosFinanceirosFilter,
        },
        true
      );
      if (requestSeq !== lastRequestSeqRef.current) return;
      setInstrumentoList(result.result);
      setRowCount(result.totalCount);
    } catch (error) {
      console.error('Erro ao buscar instrumentos:', error);
    } finally {
      if (requestSeq === lastRequestSeqRef.current) {
        setLoading(false);
      }
    }
  }, [dispatch, postInstrumentosSaldosAtivosPaginated, instrumentosFinanceirosFilter, linkOperator, page, pageSize]);

  useEffect(() => {
    getInstrumentoList();
  }, [getInstrumentoList]);

  const getQuantidadeEmitida = useCallback((row: any): number | undefined => {
    return row?.criteriosRemuneracao?.quantidadeEmitida ?? row?.quantidadeEmitida ?? row?.quantidade;
  }, []);

  const getCodigoInstrumento = useCallback((row: any): string | undefined => {
    return row?.codigo ?? row?.codigoInstrumento;
  }, []);

  const getTipoInstrumento = useCallback((row: any): string | undefined => {
    return row?.tipoInstrumentoFinanceiro ?? row?.instrumentoTipo;
  }, []);

  const getValorNominalUnitarioEmissao = useCallback((row: any): number | undefined => {
    return (
      row?.criteriosRemuneracao?.valorNominalUnitarioEmissao ??
      row?.valorNominalUnitarioEmissao ??
      row?.puEmissao
    );
  }, []);

  const getPuAtualizado = useCallback((row: any): number | undefined => {
    return row?.criteriosRemuneracao?.puAtualizado ?? row?.puAtualizado;
  }, []);

  const getEmissorCodigo = useCallback((row: any): string | undefined => {
    return row?.emissor?.codigo ?? row?.codigoEmissor;
  }, []);

  const getEmissorRazaoSocial = useCallback((row: any): string | undefined => {
    return row?.emissor?.razaoSocial ?? row?.razaoSocialEmissor ?? row?.razaoSocial;
  }, []);

  const getPuAtualizadoValue = (instrumento: InstrumentoFinanceiroApiResponse) => {
    const puAtualizado = getPuAtualizado(instrumento as any);
    return puAtualizado == undefined
      ? 'R$ - '
      : `R$ ${puAtualizado.toLocaleString(undefined, {
        minimumFractionDigits: 8,
        maximumFractionDigits: 8,
      })}`;
  };

  const getSaldoAtualizadoValue = (instrumento: InstrumentoFinanceiroApiResponse) => {
    const quantidadeEmitida = getQuantidadeEmitida(instrumento as any);
    const valorNominalUnitarioEmissao = getValorNominalUnitarioEmissao(instrumento as any);
    const puAtualizado = getPuAtualizado(instrumento as any);

    if (quantidadeEmitida == undefined || valorNominalUnitarioEmissao == undefined) {
      return formatEightDecimalsCurrency(0);
    }

    if (isParticipanteComum) {
      return formatEightDecimalsCurrency(quantidadeEmitida * valorNominalUnitarioEmissao);
    }

    return puAtualizado == undefined
      ? formatEightDecimalsCurrency(quantidadeEmitida * valorNominalUnitarioEmissao)
      : formatEightDecimalsCurrency(quantidadeEmitida * puAtualizado);
  };

  const onFilterChange = useCallback((filterModel: GridFilterModel) => {
    if (filterDebounceTimeoutRef.current) {
      window.clearTimeout(filterDebounceTimeoutRef.current);
    }

    filterDebounceTimeoutRef.current = window.setTimeout(() => {
      if (!filterModel.items) {
        setInstrumentosFinanceirosFilter([]);
        setPage(0);
        return;
      }

      setLinkOperator(filterModel?.linkOperator?.toString()?.toUpperCase() ?? 'AND');
      const newFilter = new Array<InstrumentosFinanceirosFilter>();

      filterModel.items.forEach((filter) => {
        const operatorValue =
          filter.operatorValue == 'startsWith'
            ? 'STARTS_WITH'
            : filter.operatorValue == 'endsWith'
              ? 'ENDS_WITH'
              : filter.operatorValue?.toUpperCase();

        const formattedValue = filter.value?.toString();
        if (!formattedValue) return;

        const params = {
          columnType: getColumnTypeForBackend(filter.columnField),
          operationFilterType: operatorValue,
          value: formattedValue,
        } as InstrumentosFinanceirosFilter;

        newFilter.push(params);
      });

      setInstrumentosFinanceirosFilter(newFilter);
      setPage(0);
    }, 350);
  }, [getColumnTypeForBackend]);

  useEffect(() => {
    return () => {
      if (filterDebounceTimeoutRef.current) {
        window.clearTimeout(filterDebounceTimeoutRef.current);
      }
    };
  }, []);

  const onSortChange = useCallback(
    (sortModel: GridSortModel) => {
      if (sortModel == null || sortModel.length === 0) {
        setInstrumentosFinanceirosFilter((current) =>
          current.filter(
            (x) => x.orderByType != 'ASC' && x.orderByType != 'DESC' && x.orderByType != 1 && x.orderByType != 2
          )
        );
        setPage(0);
        return;
      }

      const sort = sortModel[0];
      const orderByType = sort.sort?.toUpperCase();

      if (orderByType == undefined) {
        setInstrumentosFinanceirosFilter((current) =>
          current.filter(
            (x) => x.orderByType != 'ASC' && x.orderByType != 'DESC' && x.orderByType != 1 && x.orderByType != 2
          )
        );
        setPage(0);
        return;
      }

      const sortFilter = {
        columnType: getColumnTypeForBackend(sort.field),
        orderByType,
      } as InstrumentosFinanceirosFilter;

      setInstrumentosFinanceirosFilter((current) => {
        const withoutSort = current.filter(
          (x) => x.orderByType != 'ASC' && x.orderByType != 'DESC' && x.orderByType != 1 && x.orderByType != 2
        );
        return [...withoutSort, sortFilter];
      });
      setPage(0);
    },
    [getColumnTypeForBackend]
  );

  const filterOperators = getGridStringOperators().filter(({ value }) =>
    ['equals', 'contains', 'startsWith', 'endsWith'].includes(value)
  );

  const getGridColumns = useCallback(
    () => [
      {
        field: 'codigo',
        headerName: 'Código',
        type: 'string',
        filterOperators,
        filterable: true,
        sortable: true,
        width: 150,
        minWidth: 100,
        maxWidth: 200,
        valueGetter: (params: GridValueGetterParams) => getCodigoInstrumento(params.row as any),
        renderCell: (params: GridCellParams) => {
          const instrumento = params.row as any;
          return (
            <>
              <Typography variant="body2">{getCodigoInstrumento(instrumento) ?? '-'}</Typography>
            </>
          );
        },
      },
      {
        field: 'tipoInstrumentoFinanceiro',
        headerName: 'Tipo Instrumento',
        type: 'string',
        filterOperators,
        filterable: true,
        sortable: true,
        width: 210,
        minWidth: 100,
        maxWidth: 250,
        valueGetter: (params: GridValueGetterParams) => getTipoInstrumento(params.row as any),
        renderCell: (params: GridCellParams) => {
          const instrumento = params.row as any;
          const tipoInstrumento = getTipoInstrumento(instrumento);
          return (
            <>
              <Typography variant="body2">
                {(tipoInstrumento && (getInstrumentoFinanceiroLongDescription as any)[tipoInstrumento]) ?? tipoInstrumento ?? '-'}
              </Typography>
            </>
          );
        },
      },
      {
        field: 'emissor.razaoSocial',
        headerName: 'Emissor',
        type: 'string',
        filterOperators,
        filterable: true,
        sortable: true,
        width: 150,
        minWidth: 100,
        maxWidth: 200,
        valueGetter: (params: GridValueGetterParams) => getEmissorRazaoSocial(params.row as any),
        renderCell: (params: GridCellParams) => {
          const instrumento = params.row as any;
          return (
            <>
              <Typography variant="body2">{getEmissorRazaoSocial(instrumento)}</Typography>
            </>
          );
        },
      },
      {
        field: 'emissor.codigo',
        headerName: 'Código Emissor',
        type: 'string',
        filterOperators,
        filterable: true,
        sortable: true,
        width: 150,
        minWidth: 100,
        maxWidth: 200,
        valueGetter: (params: GridValueGetterParams) => getEmissorCodigo(params.row as any),
        renderCell: (params: GridCellParams) => {
          const instrumento = params.row as any;
          return (
            <>
              <Typography variant="body2">{getEmissorCodigo(instrumento)}</Typography>
            </>
          );
        },
      },
      {
        field: 'criteriosRemuneracao.quantidadeEmitida',
        headerName: 'Quantidade',
        type: 'string',
        width: 200,
        minWidth: 100,
        maxWidth: 500,
        valueGetter: (params: GridValueGetterParams) => getQuantidadeEmitida(params.row as any),
        renderCell: (params: GridCellParams) => {
          const instrumento = params.row as any;
          const quantidadeEmitida = getQuantidadeEmitida(instrumento);
          return (
            <>
              <Typography variant="body2">{quantidadeEmitida ?? '-'}</Typography>
            </>
          );
        },
      },
      {
        field: 'criteriosRemuneracao.valorNominalUnitarioEmissao',
        headerName: 'PU de Emissão',
        type: 'string',
        width: 200,
        minWidth: 100,
        maxWidth: 500,
        valueGetter: (params: GridValueGetterParams) => {
          const valorNominalUnitarioEmissao = getValorNominalUnitarioEmissao(params.row as any);
          return valorNominalUnitarioEmissao == undefined ? '-' : formatEightDecimalsNumber(valorNominalUnitarioEmissao);
        },
        renderCell: (params: GridCellParams) => {
          const instrumento = params.row as any;
          const valorNominalUnitarioEmissao = getValorNominalUnitarioEmissao(instrumento);
          return (
            <>
              <Typography variant="body2">
                {valorNominalUnitarioEmissao == undefined
                  ? 'R$ - '
                  : formatEightDecimalsCurrency(valorNominalUnitarioEmissao)}
              </Typography>
            </>
          );
        },
      },
      {
        field: 'criteriosRemuneracao.puAtualizado',
        headerName: 'PU Atualizado',
        type: 'string',
        hide: true,
        width: 150,
        minWidth: 100,
        maxWidth: 200,
        valueGetter: (params: GridValueGetterParams) =>
          getPuAtualizadoValue(params.row as InstrumentoFinanceiroApiResponse),
        renderCell: (params: GridCellParams) => {
          const instrumento = params.row as InstrumentoFinanceiroApiResponse;
          return (
            <>
              <Typography variant="body2">{getPuAtualizadoValue(instrumento)}</Typography>
            </>
          );
        },
      },
      {
        field: 'saldoAtualizado',
        headerName: 'Saldo Atualizado',
        type: 'string',
        width: 150,
        minWidth: 100,
        maxWidth: 200,
        hide: true,
        valueGetter: (params: GridValueGetterParams) =>
          getSaldoAtualizadoValue(params.row as InstrumentoFinanceiroApiResponse),
        renderCell: (params: GridCellParams) => {
          const instrumento = params.row as InstrumentoFinanceiroApiResponse;
          return (
            <>
              <Typography variant="body1">{getSaldoAtualizadoValue(instrumento)}</Typography>
            </>
          );
        },
      },
    ],
    [filterOperators]
  );

  return (
    <>
      <DataGridPro
        autoHeight
        localeText={localizedTextsMap}
        componentsProps={componentsPropsValues}
        rows={instrumentoList}
        getRowId={(row) => {
          const anyRow = row as any;
          const codigoBase = anyRow?.codigoInstrumento ?? anyRow?.codigo ?? '';
          const emissorBase = anyRow?.codigoEmissor ?? anyRow?.emissor?.codigo ?? '';
          return (
            anyRow?.instrumentoFinanceiroGuid ??
            (codigoBase || emissorBase ? `${codigoBase}-${emissorBase}` : undefined) ??
            anyRow?.id
          );
        }}
        columns={getGridColumns()}
        loading={loading}
        components={{
          Toolbar: CustomToolBarUtils,
        }}
        disableSelectionOnClick
        rowsPerPageOptions={rowsPerPageOptionsSaldoAtivos}
        page={page}
        rowCount={rowCount}
        pagination
        paginationMode="server"
        onPageChange={(newPage) => setPage(newPage)}
        onPageSizeChange={(newPageSize) => {
          setPageSize(newPageSize);
          setPage(0);
        }}
        pageSize={pageSize}
        filterMode="server"
        onFilterModelChange={onFilterChange}
        sortingMode="server"
        onSortModelChange={onSortChange}
      />
    </>
  );
};

export default ListaDeSaldoAtivos;
